import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, FileUp, Loader2, Send, X } from "lucide-react";
import { API, formatApiError } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const ALLOWED_EXT = ["pdf", "doc", "docx", "odt", "jpg", "jpeg", "png"];
const MAX_SIZE = 15 * 1024 * 1024;

const inputCls = "bg-white border-[#D9E0E6] text-[#1d1d1b] placeholder:text-[#70706f]/70 focus-visible:ring-2 focus-visible:ring-[#00AAA0] focus-visible:ring-offset-0 focus-visible:border-[#00AAA0] rounded-md h-12";

export default function JobApplicationForm({ jobSlug, jobTitle }) {
  const [form, setForm] = useState({ name: "", email: "", telefon: "", nachricht: "" });
  const [cv, setCv] = useState(null);
  const [cover, setCover] = useState(null);
  const [extra, setExtra] = useState([]);
  const [sending, setSending] = useState(false);
  const [successRef, setSuccessRef] = useState(null);
  const cvRef = useRef(null);
  const coverRef = useRef(null);
  const extraRef = useRef(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const validate = (file) => {
    if (!file) return null;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      toast.error(`Dateityp .${ext} nicht zulässig`, { description: "Erlaubt: PDF, DOC, DOCX, ODT, JPG, PNG" });
      return null;
    }
    if (file.size > MAX_SIZE) {
      toast.error(`${file.name} ist größer als 15 MB`);
      return null;
    }
    return file;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!cv && !cover) {
      toast.error("Bitte laden Sie mindestens den Lebenslauf oder das Anschreiben hoch");
      return;
    }
    setSending(true);
    try {
      const data = new FormData();
      data.append("job_slug", jobSlug);
      data.append("name", form.name);
      data.append("email", form.email);
      data.append("telefon", form.telefon);
      data.append("nachricht", form.nachricht);
      if (cv) data.append("lebenslauf", cv);
      if (cover) data.append("anschreiben", cover);
      extra.forEach((f) => data.append("weitere", f));
      const res = await axios.post(`${API}/bewerbungen`, data, { headers: { "Content-Type": "multipart/form-data" } });
      setSuccessRef(res.data.ref);
      toast.success("Bewerbung übermittelt", { description: "Sie erhalten eine Bestätigung per E-Mail." });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Übermittlung fehlgeschlagen");
    } finally {
      setSending(false);
    }
  };

  if (successRef) {
    return (
      <div className="border border-[#00AAA0]/40 bg-white rounded-lg p-8 lg:p-10 shadow-[0_8px_36px_rgba(0,170,160,0.08)]" data-testid="bewerbung-success">
        <CheckCircle2 size={40} className="text-[#00AAA0]" />
        <h3 className="text-xl font-bold mt-5 text-[#1d1d1b]">Bewerbung erfolgreich übermittelt</h3>
        <p className="mt-3 text-[#1d1d1b]/80 text-sm leading-relaxed max-w-md">Vielen Dank für Ihr Interesse. Sie erhalten in Kürze eine Empfangsbestätigung per E-Mail.</p>
        <div className="mt-6 inline-block border border-[#E3E8EC] bg-[#F7F9FA] rounded-md px-5 py-3">
          <span className="font-tech text-[10px] tracking-[0.25em] uppercase text-[#70706f] block">Referenz</span>
          <span className="font-tech text-base text-[#1d1d1b]" data-testid="bewerbung-ref">{successRef}</span>
        </div>
      </div>
    );
  }

  const FileSlot = ({ label, required, file, setter, refEl, testid, hint }) => (
    <div>
      <Label className="font-tech text-[10px] tracking-[0.22em] uppercase text-[#70706f]">{label}{required && " *"}</Label>
      <div onClick={() => refEl.current?.click()} data-testid={testid}
        className="mt-2 border-2 border-dashed border-[#C9D4DD] hover:border-[#00AAA0] rounded-md px-4 py-5 flex items-center gap-3 cursor-pointer transition-colors bg-white">
        <FileUp size={20} className="text-[#70706f] shrink-0" />
        <div className="flex-1 min-w-0">
          {file ? (
            <div className="flex items-center justify-between gap-3">
              <span className="font-tech text-xs text-[#1d1d1b] truncate">{file.name} <span className="text-[#70706f]">({(file.size / 1024).toFixed(0)} KB)</span></span>
              <button type="button" onClick={(e) => { e.stopPropagation(); setter(null); }} className="text-[#70706f] hover:text-[#00AAA0]" aria-label={`${label} entfernen`}><X size={15} /></button>
            </div>
          ) : (
            <>
              <p className="text-sm text-[#1d1d1b]">Datei auswählen <span className="text-[#2C57D2] underline underline-offset-4">durchsuchen</span></p>
              <p className="font-tech text-[10px] tracking-[0.15em] uppercase text-[#70706f] mt-1">{hint}</p>
            </>
          )}
        </div>
        <input ref={refEl} type="file" hidden accept=".pdf,.doc,.docx,.odt,.jpg,.jpeg,.png"
          onChange={(e) => { const f = validate(e.target.files[0]); if (f) setter(f); e.target.value = ""; }} />
      </div>
    </div>
  );

  return (
    <form onSubmit={submit} data-testid="bewerbung-form"
      className="border border-[#E3E8EC] bg-white rounded-lg p-8 lg:p-10 shadow-[0_4px_28px_rgba(17,24,39,0.05)]">
      <p className="font-tech text-[11px] tracking-[0.3em] uppercase text-[#00AAA0]">Bewerbung</p>
      <h2 className="text-2xl font-bold text-[#1d1d1b] mt-2">Direkt bewerben</h2>
      <p className="mt-3 text-sm text-[#1d1d1b]/80 leading-relaxed">Laden Sie Ihre Unterlagen sicher über dieses Formular hoch. Ihre Daten werden ausschließlich zur Bearbeitung Ihrer Bewerbung für <strong>{jobTitle}</strong> verwendet.</p>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="bw-name" className="font-tech text-[10px] tracking-[0.22em] uppercase text-[#70706f]">Vor- und Nachname *</Label>
          <Input id="bw-name" required value={form.name} onChange={set("name")} placeholder="Max Mustermann" className={`${inputCls} mt-2`} data-testid="bewerbung-name-input" />
        </div>
        <div>
          <Label htmlFor="bw-email" className="font-tech text-[10px] tracking-[0.22em] uppercase text-[#70706f]">E-Mail *</Label>
          <Input id="bw-email" required type="email" value={form.email} onChange={set("email")} placeholder="max@example.com" className={`${inputCls} mt-2`} data-testid="bewerbung-email-input" />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="bw-telefon" className="font-tech text-[10px] tracking-[0.22em] uppercase text-[#70706f]">Telefon</Label>
          <Input id="bw-telefon" value={form.telefon} onChange={set("telefon")} placeholder="+49 …" className={`${inputCls} mt-2`} data-testid="bewerbung-telefon-input" />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <FileSlot label="Lebenslauf" required file={cv} setter={setCv} refEl={cvRef} testid="bewerbung-cv-slot" hint="PDF / DOCX · max. 15 MB" />
        <FileSlot label="Anschreiben" file={cover} setter={setCover} refEl={coverRef} testid="bewerbung-cover-slot" hint="Optional · PDF / DOCX" />
      </div>

      <div className="mt-5">
        <Label className="font-tech text-[10px] tracking-[0.22em] uppercase text-[#70706f]">Weitere Zeugnisse / Zertifikate</Label>
        <div onClick={() => extraRef.current?.click()} data-testid="bewerbung-extra-slot"
          className="mt-2 border-2 border-dashed border-[#C9D4DD] hover:border-[#00AAA0] rounded-md px-4 py-4 flex items-center gap-3 cursor-pointer transition-colors bg-white">
          <FileUp size={18} className="text-[#70706f]" />
          <p className="text-sm text-[#1d1d1b]">Optional – mehrere Dateien möglich <span className="text-[#2C57D2] underline underline-offset-4">durchsuchen</span></p>
          <input ref={extraRef} type="file" hidden multiple accept=".pdf,.doc,.docx,.odt,.jpg,.jpeg,.png"
            onChange={(e) => {
              const valids = Array.from(e.target.files).map(validate).filter(Boolean);
              setExtra((prev) => [...prev, ...valids].slice(0, 6));
              e.target.value = "";
            }} />
        </div>
        {extra.length > 0 && (
          <ul className="mt-3 space-y-2">
            {extra.map((f, i) => (
              <li key={`${f.name}-${i}`} className="flex items-center justify-between border border-[#E3E8EC] bg-[#F7F9FA] rounded-md px-3 py-2">
                <span className="font-tech text-xs text-[#1d1d1b] truncate">{f.name} <span className="text-[#70706f]">({(f.size / 1024).toFixed(0)} KB)</span></span>
                <button type="button" onClick={() => setExtra((p) => p.filter((_, j) => j !== i))} className="text-[#70706f] hover:text-[#00AAA0]" aria-label={`${f.name} entfernen`}><X size={14} /></button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-6">
        <Label htmlFor="bw-nachricht" className="font-tech text-[10px] tracking-[0.22em] uppercase text-[#70706f]">Nachricht / Motivation</Label>
        <Textarea id="bw-nachricht" value={form.nachricht} onChange={set("nachricht")} rows={4}
          placeholder="Warum passen Sie zu uns? Verfügbarkeit, Gehaltsvorstellung, etc." data-testid="bewerbung-nachricht-textarea"
          className="bg-white border-[#D9E0E6] text-[#1d1d1b] placeholder:text-[#70706f]/70 focus-visible:ring-2 focus-visible:ring-[#00AAA0] focus-visible:ring-offset-0 rounded-md mt-2" />
      </div>

      <button type="submit" disabled={sending} data-testid="bewerbung-submit-btn"
        className="mt-7 w-full flex items-center justify-center gap-3 h-13 py-4 rounded-md bg-[#00AAA0] hover:bg-[#00958c] disabled:opacity-60 text-white font-tech text-xs tracking-[0.22em] uppercase transition-all active:scale-[0.99]">
        {sending ? (<><Loader2 size={16} className="animate-spin" /> Wird übermittelt…</>) : (<><Send size={16} /> Bewerbung sicher absenden</>)}
      </button>
      <p className="mt-4 text-xs text-[#70706f] leading-relaxed">Mit dem Absenden bestätigen Sie, dass Ihre Daten gemäß unserer Datenschutzerklärung ausschließlich für die Bearbeitung Ihrer Bewerbung verwendet werden.</p>
    </form>
  );
}
